import React, { useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  FormGroup
} from '@mui/material';
import { useTheme } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useWorkSchedule } from '../../context/useTimeClock';
import { WORK_SCHEDULE_TYPE_OPTIONS, WEEKLY_DAYS_OPTIONS } from '../../constants/timeclockConstants';
import type { WorkScheduleFormData } from '../../types/timeclock.types';
import { UserSelect } from '../../../../shared/components/SelectsDinamycs/UserSelect';
import { UserRole } from '../../../../shared/constants/roles';
import { userService } from '../../../../shared/services/users/usersServices';

const validationSchema = Yup.object({
  userIds: Yup.array()
    .of(Yup.object({
      id: Yup.string().required(),
      name: Yup.string().required(),
      email: Yup.string().optional(),
    }))
    .min(1, 'Selecione pelo menos um funcionário')
    .required('Funcionário é obrigatório'),
  type: Yup.string().required('Tipo é obrigatório'),
  name: Yup.string().required('Nome da jornada é obrigatório'),
  startTime: Yup.string().required('Horário de início é obrigatório'),
  endTime: Yup.string().required('Horário de fim é obrigatório'),
  weeklyDays: Yup.array().min(1, 'Selecione pelo menos um dia da semana'),
  description: Yup.string().optional()
});

export function WorkScheduleForm() {
  const theme = useTheme();
  const { id } = useParams();
  const {
    workScheduleData,
    createWorkSchedule,
    updateWorkSchedule,
    isSubmitting,
    error,
    setError
  } = useWorkSchedule();

  const isEditing = !!id;


  const [userOptions, setUserOptions] = React.useState<any[]>([]);

  useEffect(() => {
    async function fetchUsers() {
      const res = await userService.list({});
      setUserOptions(res.data || []);
    }
    if (isEditing && id) {
      fetchUsers();
    }
  }, [isEditing, id]);

  const initialUserIds = React.useMemo(() => {
    if (isEditing && workScheduleData?.userId && userOptions.length > 0) {
      return userOptions.filter(u => u.id === workScheduleData.userId);
    }
    return [];
  }, [isEditing, workScheduleData?.userId, userOptions]);

  const formik = useFormik<WorkScheduleFormData>({
    initialValues: {
      userIds: initialUserIds,
      type: workScheduleData?.type || 'REGULAR',
      name: workScheduleData?.name || '',
      startTime: workScheduleData?.startTime || '',
      endTime: workScheduleData?.endTime || '',
      weeklyDays: workScheduleData?.weeklyDays || [],
      description: workScheduleData?.description || ''
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setError(null);

        
        const validTypes = ['FIXED', 'ESCALA', 'FLEX'];
        const type = validTypes.includes(values.type) ? values.type : 'FIXED';

        
        const formatTime = (time: string) => time ? time.slice(0, 5) : '';

        let payload;
        if (isEditing && id) {
          payload = {
            ...values,
            type,
            startTime: formatTime(values.startTime),
            endTime: formatTime(values.endTime),
            userIds: Array.isArray(values.userIds) && values.userIds.length > 0
              ? [values.userIds[0]]
              : [],
          };
          delete (payload as Partial<typeof payload>).userIds;
          await updateWorkSchedule(id, payload);
        } else {
          payload = {
            ...values,
            type,
            startTime: formatTime(values.startTime),
            endTime: formatTime(values.endTime),
            userIds: Array.isArray(values.userIds)
              ? values.userIds.map((u: any) => u.id)
              : [],
          };
          await createWorkSchedule(payload);
        }
      } catch (error) {

      }
    }
  });

  const handleWeeklyDaysChange = (day: string, checked: boolean) => {
    const currentDays = formik.values.weeklyDays;
    const updatedDays = checked
      ? [...currentDays, day]
      : currentDays.filter(d => d !== day);

    formik.setFieldValue('weeklyDays', updatedDays);
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {typeof error === 'string'
            ? error
            : Array.isArray((error as { message?: { message: string[] } }).message?.message)
              ? (error as { message?: { message: string[] } }).message!.message.map((msg: string, idx: number) => <div key={idx}>{msg}</div>)
              : (error as { message?: { message: string } }).message?.message || 'Ocorreu um erro.'}
        </Alert>
      )}



      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <UserSelect
            multiple
            
            value={formik.values.userIds as any}
            onChange={users => formik.setFieldValue('userIds', users)}
            label="Funcionários"
            typeFlag={[UserRole.admin,
            UserRole.manager,
            UserRole.employee,
            UserRole.outsourced,
            UserRole.customer,
            UserRole.guest,]}
            disabled={isSubmitting || !!id}
          />
          {formik.touched.userIds && formik.errors.userIds && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.userIds}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="type-label">Tipo de Jornada</InputLabel>
            <Select
              labelId="type-label"
              id="type"
              name="type"
              value={formik.values.type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.type && Boolean(formik.errors.type)}
              disabled={isSubmitting}
              label="Tipo de Jornada"
            >
              {WORK_SCHEDULE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="name"
            name="name"
            label="Nome da Jornada"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            disabled={isSubmitting}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="startTime"
            name="startTime"
            label="Horário de Início"
            type="time"
            value={formik.values.startTime}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.startTime && Boolean(formik.errors.startTime)}
            helperText={formik.touched.startTime && formik.errors.startTime}
            disabled={isSubmitting}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="endTime"
            name="endTime"
            label="Horário de Fim"
            type="time"
            value={formik.values.endTime}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.endTime && Boolean(formik.errors.endTime)}
            helperText={formik.touched.endTime && formik.errors.endTime}
            disabled={isSubmitting}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom color={theme.palette.text.primary}>
            Dias da Semana
          </Typography>
          <FormGroup row>
            {WEEKLY_DAYS_OPTIONS.map((day) => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    checked={formik.values.weeklyDays.includes(day.value)}
                    onChange={(e) => handleWeeklyDaysChange(day.value, e.target.checked)}
                    disabled={isSubmitting}
                  />
                }
                label={day.label}
              />
            ))}
          </FormGroup>
          {formik.touched.weeklyDays && formik.errors.weeklyDays && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.weeklyDays}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            name="description"
            label="Descrição"
            multiline
            rows={4}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            disabled={isSubmitting}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !formik.isValid}
            >
              {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}